import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown, ChevronUp, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useRecipeLabelTemplate,
  useUpdateRecipeLabelTemplate,
} from "@/hooks/useLabelTemplate";
import { useContainerFlags } from "@/hooks/useContainerFlags";
import { useOrgBranding } from "@/hooks/useOrgBranding";
import { getApiErrorMessage } from "@/lib/api/api";
import { flagMeaning } from "@/lib/container-flags";
import type { LabelFieldKey } from "@/types/label-template";
import type { Recipe, RecipeShelfLife } from "@/types/recipe";
import { LabelFieldPicker } from "./LabelFieldPicker";
import { LabelPreview } from "./LabelPreview";
import { buildLabelDocument, formatAllergens } from "./label-print";
import { labelFieldValue, sampleBatchId, type LabelFieldValues } from "./label-fields";
import { labelUseBy } from "./use-by";
import { openPrintWindow } from "./print-window";
import { useLabelFieldNames } from "./useLabelFieldNames";
import { useLabelSizeNames } from "./useLabelSizeNames";

interface RecipeLabelPanelProps {
  recipe: Recipe;
  /** The shelf-life row the label draws its use-by and storage from, if any. */
  shelfLife?: RecipeShelfLife;
  canManage?: boolean;
  /**
   * Colours flagged on this recipe's containers and tools (T-1005). Passed in
   * rather than fetched here: the panel already knows which recipe it is for,
   * but which containers that recipe uses is the containers tab's question.
   */
  containerFlags?: readonly string[];
  /** Fixed so a preview does not change under a test or a screenshot. */
  today?: Date;
}

/**
 * What this recipe's label prints.
 *
 * The field list comes from the tenant's template (T-1001), so changing it in
 * Settings changes this panel without anyone editing the recipe. A recipe that
 * genuinely differs can override — and can hand the decision back.
 */
export function RecipeLabelPanel({
  recipe,
  shelfLife,
  canManage = false,
  containerFlags,
  today,
}: RecipeLabelPanelProps) {
  const { t } = useLingui();
  const branding = useOrgBranding();
  const names = useLabelFieldNames();
  const sizeNames = useLabelSizeNames();
  const [open, setOpen] = useState(true);
  const [draft, setDraft] = useState<LabelFieldKey[] | null>(null);

  const { data, isLoading } = useRecipeLabelTemplate(recipe.id);
  const { data: flags } = useContainerFlags();
  const save = useUpdateRecipeLabelTemplate(recipe.id);

  const fields = draft ?? data?.fields ?? [];
  const now = today ?? new Date();

  // One dot per colour, not per container: three red-lidded tubs are one thing
  // to know about, and a label has no room to say it three times.
  const flagColors = [...new Set(containerFlags ?? [])];
  const flagMeanings = flagColors
    .map((color) => flagMeaning(flags?.codes, color))
    .filter((m): m is string => m != null);

  const values: LabelFieldValues = {
    RECIPE_NAME: recipe.name,
    PREP_DATE: now.toLocaleDateString(),
    // Computed from the stage's shelf life when it has one, so a two hour hot
    // hold prints a time this afternoon rather than a rule nobody can act on
    // (T-1004). A stage with only the old typed rule still prints that.
    USE_BY: labelUseBy(shelfLife, now),
    // A dash here would read as "not filled in", which is the one thing an
    // allergen line must never be ambiguous about (T-1003).
    ALLERGENS: formatAllergens(
      recipe.allergens?.map((a) => a.name),
      t`None listed`,
    ),
    STORAGE_METHOD: shelfLife?.storageMethod,
    PREPARED_BY: null,
    BATCH_ID: sampleBatchId(recipe.id, now),
    CONTAINER_FLAG: flagMeanings.join(", ") || null,
  };

  const size = data?.size ?? "1x3";

  const print = () =>
    openPrintWindow(
      buildLabelDocument({
        fields,
        values,
        fieldNames: names,
        title: recipe.name,
        size,
        sizes: data?.availableSizes,
        flagColors,
        copy: { documentTitle: t`Label: ${recipe.name}`, allergensPrefix: t`Contains:` },
        branding,
      }),
    );

  const commit = (next: LabelFieldKey[] | null) =>
    save.mutate(next ?? [], {
      onSuccess: () => {
        setDraft(null);
        toast.success(next ? t`Label fields saved for this recipe` : t`Recipe now follows the organization labels`);
      },
      onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to save the label fields`)),
    });

  return (
    <div className="flex flex-col gap-3 rounded-[10px] border border-(--color-border)/60 bg-(--color-surface-muted)/40 p-3">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-[16px] font-medium">{t`Labeling`}</p>
          <p className="text-sm text-(--color-text-secondary)">
            {isLoading
              ? t`Loading the label…`
              : data?.inherited
                ? t`Following the organization's label settings.`
                : t`This recipe prints its own set of fields.`}
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          className="size-11 shrink-0 rounded-[10px]"
          aria-label={open ? t`Collapse labeling` : t`Expand labeling`}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
        </Button>
      </div>

      {open && (
        <>
          <Separator />

          {isLoading || !data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <div className="flex items-start gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[18px] font-medium tracking-tight">{t`Details`}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-[13px] text-(--color-text-secondary)">
                      {t`Prints on ${sizeNames[size] ?? size}`}
                    </span>
                    <Button size="sm" className="h-11 gap-2 rounded-[10px]" onClick={print}>
                      <Printer className="size-5" />
                      {t`Print label`}
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {fields.map((field) => (
                    <div
                      key={field}
                      className="flex items-center justify-between gap-3 rounded-lg border border-(--color-border) bg-(--color-surface-page) px-3 py-3 text-[14px]"
                    >
                      <span className="text-(--color-text-secondary)">{names[field]}</span>
                      <span className="truncate font-medium">{labelFieldValue(field, values)}</span>
                    </div>
                  ))}
                </div>

                {canManage &&
                  (draft ? (
                    <div className="flex flex-col gap-3">
                      <LabelFieldPicker
                        available={data.available}
                        selected={draft}
                        onChange={setDraft}
                        disabled={save.isPending}
                      />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => commit(draft)} disabled={save.isPending}>
                          {save.isPending && <Loader2 className="size-4 animate-spin" />}
                          {t`Save for this recipe`}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDraft(null)}
                          disabled={save.isPending}
                        >
                          {t`Cancel`}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setDraft(data.fields)}>
                        {t`Customize for this recipe`}
                      </Button>
                      {!data.inherited && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => commit(null)}
                          disabled={save.isPending}
                        >
                          {t`Use organization labels`}
                        </Button>
                      )}
                    </div>
                  ))}
              </div>

              <div className="flex shrink-0 flex-col gap-3 rounded-[10px] border border-(--color-border) bg-(--color-surface-page) p-4">
                <div className="flex flex-col gap-1">
                  <p className="text-[16px] font-medium tracking-tight">{t`Preview Example`}</p>
                  <p className="text-[14px] tracking-tight text-(--color-text-secondary)">
                    {t`Sample label based on current settings`}
                  </p>
                </div>
                <LabelPreview
                  fields={fields}
                  values={values}
                  title={recipe.name}
                  flagColors={flagColors}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
